import * as Yup from 'yup';

import { Box, Checkbox, DialogTitle, Grid, IconButton, InputLabel, Link, Tooltip } from '@material-ui/core';
import { Form, Formik } from 'formik';
import {
  AddressField,
  NumberField,
  PhoneField,
  SelectField,
  SubmitButton,
  TextField,
  CheckboxField,
  Section
} from '@microrealestate/commonui/components';
import React, { useCallback, useContext, useMemo } from 'react';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { StoreContext } from '../../store';
import types from './types';
import useDialog from '../../hooks/useDialog';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import FacilityCheckbox from '@microrealestate/commonui/components/FormFields/FacilityCheckbox';
import { HelpOutline as HelpOutlineIcon } from '@material-ui/icons';
import { newPropertyFormSteps } from '../shepherdStep/guideStep';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const facilityOptions = [
  'Balcon',
  'CCTV',
  'Dapur',
  'Dispenser',
  'Jemuran',
  'Jual Makanan',
  'Kompor',
  'Kulkas',
  'Laundry',
  'Locker',
  'Mushola',
  'Penjaga Kos',
  'K. Mandi Dalam',
  'K. Mandi Luar',
  'R. Cuci',
  'R. Jemur',
  'R. Keluarga',
  'R. Makan',
  'R. Santai',
  'R. Tamu',
  'Rice Cooker',
  'Rooftop',
  'TV',
  'Taman',
  'Wifi',
  'Gym',
  'Kolam Renang',
  'Kantin',
  'Gazebo',
  'Mesin Cuci',
]
const facilityOptionsRoom = [
  'AC',
  'Bantal',
  'Dapur Pribadi',
  'Dispenser',
  'Guling',
  'Jendela',
  'Kasur',
  'Kipas Angin',
  'Kulkas',
  'Kursi',
  'Lemari Baju',
  'Meja',
  'Sofa',
  'TV',
  'Tidak ada Kasur',
  'Ventilasi',
  'TV Kabel',
  'Netflix',
  'Microwave',
  'Listrik Token',
]
const facilityOptionsBathroom = [
  'Bathtub',
  'Kloset Duduk',
  'Kloset Jongkok',
  'Shower',
  'Water Heater',
  'Bak Mandi',
  'Westafel',
]
const facilityOptionsParking = [
  'Parkir Mobil',
  'Parkir Motor',
  'Parkir Sepeda'
];

const validationSchema = (t) => Yup.object().shape({
  name: Yup.string().required(t("Name is a required field")),
  phone: Yup.string()
    .required(t("Phone is a required field"))
    .test(
      'is-valid-whatsapp',
      t("Phone must be at least 8 characters"),
      (value) => {
        if (!value) return false;
        const phoneNumber = parsePhoneNumberFromString(value);
        if (!phoneNumber) return false;
        const nationalNumber = phoneNumber.nationalNumber || '';
        return nationalNumber.length >= 8;
      }),
  description: Yup.string(),
  facilities: Yup.array(),
  facilitiesRoom: Yup.array(),
  facilitiesBathroom: Yup.array(),
  facilitiesParking: Yup.array(),
  address: Yup.object().shape({
    street1: Yup.string().required(t("Address is a required field")),
    zipCode: Yup.string().required(t("Zip code is a required field")),
    village: Yup.string().required(t("Village is a required field")),
    subDistrict: Yup.string().required(t("SubDistrict is a required field")),
    district: Yup.string().required(t("District is a required field")),
    province: Yup.string().required(t("Province is a required field")),
  }),
})


const initialValues = {
  name: '',
  phone: '',
  description: '',
  address: {
    street1: '',
    zipCode: '',
    village: '',
    subDistrict: '',
    district: '',
    province: '',
    lat: 0,
    lon: 0,
  },
  facilities: [],
  facilitiesRoom: [],
  facilitiesBathroom: [],
  facilitiesParking: [],
  bankName: '',
  accountNumber: '',
  accountName: '',
};

function NewPropertyDialog({ open, setOpen, backPage, backPath }) {
  const { t } = useTranslation('common');
  const store = useContext(StoreContext);
  const router = useRouter();
  let dist = 0;
  let subDist = 0;
  let prov = 0;
  let vill = 0;

  if (store.property.selected !== undefined) {
    if (store.property.selected.address !== undefined) {
      if (store.property.selected.address.province !== undefined) {
        dist = store.property.selected.address.district.id;
        subDist = store.property.selected.address.subDistrict.id;
        prov = store.property.selected.address.province.id;
        vill = store.property.selected.address.village.id;
      }
    }
  }

  const onStartTour = useCallback(() => {
    const tourSteps = newPropertyFormSteps(store, router, t)
    store.guideTour.setupTour(tourSteps)
    store.guideTour.startTour(store.user)
  }, [store, router, t]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const _onSubmit = useCallback(
    async (propertyPart) => {
      const updatedAddresses = {
        street1: propertyPart.address.street1,
        zipCode: propertyPart.address.zipCode,
        district: {
          id: propertyPart.address.district.split('-')[0],
          name: propertyPart.address.district.split('-')[1]
        },
        province: {
          id: propertyPart.address.province.split('-')[0],
          name: propertyPart.address.province.split('-')[1]
        },
        subDistrict: {
          id: propertyPart.address.subDistrict.split('-')[0],
          name: propertyPart.address.subDistrict.split('-')[1]
        },
        village: {
          id: propertyPart.address.village.split('-')[0],
          name: propertyPart.address.village.split('-')[1]
        },
        lat: propertyPart.address.lat || 0,
        lon: propertyPart.address.lon || 0,
      };

      const Property = {
        name: propertyPart.name,
        phone: propertyPart.phone,
        description: propertyPart.description,
        address: updatedAddresses,
        phoneNumber: 0,
        landingPage: {
          showPage: false,
          showAddress: [],
          descriptionPage: '',
          rules: '',
        },
        facility: propertyPart.facilities,
        facilityRoom: propertyPart.facilitiesRoom,
        facilityBathroom: propertyPart.facilitiesBathroom,
        facilityParking: propertyPart.facilitiesParking,
        services: [],
        expenses: [],
        transferDetail: {
          bankName: propertyPart.bankName || '',
          accountNumber: propertyPart.accountNumber || '',
          accountName: propertyPart.accountName || '',
        }
      };

      const { status, data } = await store.property.create(Property);
      if (status !== 200) {
        switch (status) {
          case 422:
            // Check if it's property limit error or other validation error
            if (data?.error_code === "MAX_PROPERTIES_REACHED") {
              return store.pushToastMessage({
                message: t('You have reached the maximum property limit for your plan. Upgrade your plan to add more properties.'),
                severity: 'error',
              });
            } else {
              return store.pushToastMessage({
                message: t('Property name is missing'),
                severity: 'error',
              });
            }
          case 403:
            return store.pushToastMessage({
              message: t('You are not allowed to add a property'),
              severity: 'error',
            });
          case 409:
            return store.pushToastMessage({
              message: t('The property already exists'),
              severity: 'error',
            });
          default:
            return store.pushToastMessage({
              message: t('Something went wrong'),
              severity: 'error',
            });
        }
      } else {
        store.pushToastMessage({
          message: t("Success create property's data"),
          severity: 'success',
        });
      }

      handleClose();

      store.property.setSelected(data);
      if (store.property.items.length == 1) {
        // For starter plan users, redirect to properties instead of rooms
        if (store.user.plan === 'starter') {
          // Redirect to properties list without specifying a specific property
          await router.push(`/properties`);
        } else {
          await router.push({
            pathname: `/rooms`
          });
        }
      } else {
        // For starter plan users, use a valid tab index (0 or 1) instead of 3
        if (store.user.plan === 'starter') {
          // Use tab index 0 (Edit Property) which should be available for starter plan
          await router.push({
            pathname: `/properties/${data._id}/${encodeURI(backPage)}/${encodeURIComponent(backPath)}`,
            query: { activeTabIndex: 0 },
          });
        } else {
          await router.push({
            pathname: `/properties/${data._id}/${encodeURI(backPage)}/${encodeURIComponent(backPath)}`,
            query: { activeTabIndex: 3 },
          });
        }
      }
    },
    [store, handleClose, router, backPage, backPath, t]
  );


  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={handleClose}
      aria-labelledby="new-property-dialog"
      PaperProps={{
        className: 'shepherd-newPropertyDialog'
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box className='shepherd-newPropertyDialog-title'>
            {t('Add a new property')}
          </Box>
          {store.guideTour.canShowGuide(store.user) && (
            <Tooltip title={t('Guide')}>
              <IconButton
                aria-label="guide"
                color="default"
                onClick={onStartTour}
              // disabled={true}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>

      <Box p={1}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(t)}
          onSubmit={_onSubmit}
        >
          {({ isSubmitting, setFieldValue, values }) => {
            return (
              <Form autoComplete="off">
                <DialogContent>
                  <Grid container spacing={1} style={{ marginBottom: '20px' }}>
                    <Grid item xs={12} className='shepherd-newPropertyDialog-nameField'>
                      <TextField label={t('Property Name') + ' *'} name="name" />
                    </Grid>
                    <Grid item xs={12} className='shepherd-newPropertyDialog-phoneField'>
                      {/* <NumberField label={t('Phone')+' *'} name="phone" /> */}
                      <PhoneField label={t('Phone_property')} name="phone" />
                    </Grid>
                    <Grid item xs={12} className='shepherd-newPropertyDialog-noteField'>
                      <TextField label={t('Note')} name="description" />
                    </Grid>
                    <Grid item xs={12} className='shepherd-newPropertyDialog-addressField' style={{ marginTop: '20px' }}>
                      <AddressField
                        dist={dist}
                        subDist={subDist}
                        prov={prov}
                        isDialog={true}
                        markerPosition={{
                          lat: values.address.lat || 0,
                          lng: values.address.lon || 0
                        }}
                        onPositionChange={(position) => {
                          setFieldValue('address.lat', position.lat);
                          setFieldValue('address.lon', position.lng);
                        }}
                        showMap={true}
                      />
                    </Grid>
                  </Grid>
                  {store.guideTour.canShowGuide(store.user) && (
                    <Section label={t('Bank Transfer Information for Rent Receipt')} className="shepherd-newPropertyDialog-bankInformation">
                      <Grid container spacing={1}>
                        <Grid item xs={12} className='shepherd-newPropertyDialog-propertyCheckBoxes'>
                          <TextField label={t('Bank name')} name="bankName" />
                        </Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={12} className='shepherd-newPropertyDialog-roomCheckBoxes'>
                          <TextField label={t('Account number')} name="accountNumber" />
                        </Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={12} className='shepherd-newPropertyDialog-bathRoomCheckBoxes'>
                          <TextField label={t('Account name')} name="accountName" />
                        </Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          {t('To receive payments via QRIS and virtual accounts, click')}
                          <Link
                            href="https://app.superkos.id/settings"
                            target="_blank"
                            rel="noopener noreferrer"
                            color='blue'
                            underline='none'
                            style={{ marginLeft: "5px", marginRight: "5px" }}
                          >
                            {t('here')}
                          </Link>
                          {t('for payment gateway configuration.')}
                        </Grid>
                      </Grid>
                    </Section>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} color="primary" className='shepherd-cancelButton'>
                    {t('Cancel')}
                  </Button>
                  <SubmitButton
                    label={!isSubmitting ? t('Add') : t('Adding property')}
                    className='shepherd-submitButton'
                  />
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Dialog>
  );
}

export default function useNewPropertyDialog() {
  return useDialog(NewPropertyDialog);
}
